import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { getEvents } from './events';
import type { EventListItemDto, GetEventsV1Params } from '@/services/platform/api/model';

/** Max page size accepted by the API — fetched in one page so date filtering stays client-side. */
const DEFAULT_PAGE_SIZE = 200;

export interface UseEventsResult {
  events: EventListItemDto[];
  totalItems: number;
  isLoading: boolean;
  hasFetched: boolean;
  refetch: () => Promise<void>;
}

/**
 * Fetches the events list for the given server-side params (hub / event type).
 * Follows the plain state-and-effect pattern used by `useShopProduct`.
 * Note: single-page fetch for now; infinite scroll is a later enhancement.
 */
export function useEvents(params?: GetEventsV1Params): UseEventsResult {
  const locale = useLocaleStore(state => state.locale);
  const [events, setEvents] = useState<EventListItemDto[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  // Serialise params so the effect only re-runs on a genuine value change.
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  /**
   * The request currently in flight, so starting another one cancels it. Without this a slow
   * earlier response can land after a newer one and win — harmless while the params only changed
   * between screens, but the ELP now drives the campaigns list from the selected day, where the
   * race shows up as the wrong day's cards after a burst of taps.
   */
  const inFlightRef = useRef<AbortController | null>(null);

  const refetch = useCallback(async () => {
    inFlightRef.current?.abort();
    const controller = new AbortController();
    inFlightRef.current = controller;

    setIsLoading(true);
    try {
      const result = await getEvents(
        { pageSize: DEFAULT_PAGE_SIZE, ...(params ?? {}) },
        locale,
        controller.signal
      );
      if (controller.signal.aborted) return;
      setEvents(result?.items ?? []);
      setTotalItems(result?.totalItems ?? 0);
    } catch (error) {
      /*
       * An abort is this hook superseding itself (or unmounting), not a failure: leave the
       * previous data alone rather than blanking the list — stale cards beat an empty flash, and
       * the request that replaced this one owns the loading state from here. Same handling as
       * `useEventsByDay`.
       */
      if (controller.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;
      console.error('useEvents: failed to fetch events', error);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
        setHasFetched(true);
      }
    }
    // paramsKey captures the params object contents; params itself is intentionally omitted.
  }, [paramsKey, locale]);

  useEffect(() => {
    refetch();
    return () => inFlightRef.current?.abort();
  }, [refetch]);

  return { events, totalItems, isLoading, hasFetched, refetch };
}
